import { useSyncExternalStore } from 'react'
import type { Question } from '../types/question'
import type { TextStructure } from '../types/textstructure'

// Module-level modal store. Mirrors `Toasts.ts`: anything (component,
// service, websocket callback) can `import { openScoreDetailsModal }
// from '../services/Modals'` and fire a modal, and a single mount
// point (`ModalHost`) renders whatever is currently open. Keeps small
// view-only popouts decoupled from the components that trigger them
// - ScoreArc and TraceArc just say "open this", nothing else.

export type ModalEntry =
  | {
    id: string
    kind: 'scoreDetails'
    question: Question | null | undefined
    prevQuestion?: Question | null
    labelLeft?: string
    labelRight?: string
    title?: string
  }
  | {
    id: string
    kind: 'lowTraceSentences'
    structure: TextStructure | null | undefined
    simint?: number | null
    prevStructure?: TextStructure | null
    prevSimint?: number
    labelLeft?: string
    labelRight?: string
    title?: string
    threshold?: number
  }

let modals: ModalEntry[] = []
const listeners = new Set<() => void>()

function notify(): void {
  listeners.forEach(l => { try { l() } catch { /* one bad listener shouldn't break the rest */ } })
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

// Open the read-only score-details popout. Returns the modal id so the
// caller can close it programmatically if needed; the modal also wires
// its own onClose to remove itself.
export function openScoreDetailsModal(opts: {
  question: Question | null | undefined
  prevQuestion?: Question | null
  labelLeft?: string
  labelRight?: string
  title?: string
}): string {
  const id = newId()
  modals = [...modals, {
    id,
    kind: 'scoreDetails',
    question: opts.question,
    prevQuestion: opts.prevQuestion,
    labelLeft: opts.labelLeft,
    labelRight: opts.labelRight,
    title: opts.title,
  }]
  notify()
  return id
}

// Open the low-trace-sentences popout for a given structure. Same
// pattern - returns the id; closing happens via `closeModal(id)` or
// the modal's own close button.
export function openLowTraceSentencesModal(opts: {
  structure: TextStructure | null | undefined
  simint?: number | null
  prevStructure?: TextStructure | null
  prevSimint?: number
  labelLeft?: string
  labelRight?: string
  title?: string
  threshold?: number
}): string {
  const id = newId()
  modals = [...modals, {
    id,
    kind: 'lowTraceSentences',
    structure: opts.structure,
    simint: opts.simint,
    prevStructure: opts.prevStructure,
    prevSimint: opts.prevSimint,
    labelLeft: opts.labelLeft,
    labelRight: opts.labelRight,
    title: opts.title,
    threshold: opts.threshold,
  }]
  notify()
  return id
}

export function closeModal(id: string): void {
  if (!modals.some(m => m.id === id)) return
  modals = modals.filter(m => m.id !== id)
  notify()
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

// React hook for the single rendering surface (ModalHost).
export function useModals(): ModalEntry[] {
  return useSyncExternalStore(subscribe, () => modals, () => modals)
}
